import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowLeft, BookOpen, ListChecks, Loader2, ScrollText, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/stores/app-store';
import { showErrorToast, showSuccessToast } from '@/hooks/use-mutation-with-toast';
import { EventsTab } from '@/components/accounting/posting-rules/events-tab';
import { DefaultsTab } from '@/components/accounting/posting-rules/defaults-tab';
import { SettingsTab } from '@/components/accounting/posting-rules/settings-tab';
import { LogTab } from '@/components/accounting/posting-rules/log-tab';
import { GLOBAL_EVENT, langOf, toAccountOptions, type PostingApp } from '@/components/accounting/posting-rules/model';

type TabId = 'events' | 'defaults' | 'settings' | 'log';
const TABS: readonly TabId[] = ['events', 'defaults', 'settings', 'log'];

interface PostingRulesPageProps {
  app: PostingApp;
}

/**
 * Muhasebe Entegrasyon Tanımları — the accountant's routing table for one
 * app: which GL account every leg of every auto-posted business event flows
 * to, global role defaults, per-event on/off switches, the revenue
 * recognition policy (travel) and the posting-exceptions log.
 */
export function PostingRulesPage({ app }: PostingRulesPageProps) {
  const { t, i18n } = useTranslation('postingRules');
  const lang = langOf(i18n.language);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabId = (TABS as readonly string[]).includes(tabParam ?? '') ? (tabParam as TabId) : 'events';
  const selectedEvent = searchParams.get('event');

  const setParam = (key: string, value: string | null) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (value === null) params.delete(key);
        else params.set(key, value);
        return params;
      },
      { replace: true }
    );

  const canEdit = useAppStore((s) => s.hasPermissionForApp(app, 'ACCOUNTING', 'EDIT'));
  const utils = trpc.useUtils();
  const { data, isLoading, error } = trpc.postingRules.overview.useQuery({ app });

  const travelAccounts = trpc.account.list.useQuery({ page: 1, limit: 2000, isActive: true }, { enabled: app === 'TRAVEL' });
  const tradingAccounts = trpc.tradingAccount.list.useQuery({ page: 1, limit: 2000, isActive: true }, { enabled: app === 'TRADING' });
  const accountOptions = useMemo(
    () => toAccountOptions((app === 'TRAVEL' ? travelAccounts.data?.accounts : tradingAccounts.data?.accounts) ?? []),
    [app, travelAccounts.data, tradingAccounts.data]
  );

  const [busyKey, setBusyKey] = useState<string | null>(null);
  const invalidate = () => utils.postingRules.overview.invalidate({ app });

  const upsert = trpc.postingRules.upsert.useMutation({
    onSuccess: () => {
      showSuccessToast(t('toast.saved'));
      invalidate();
    },
    onError: (e) => showErrorToast(e.message),
    onSettled: () => setBusyKey(null),
  });
  const remove = trpc.postingRules.remove.useMutation({
    onSuccess: () => {
      showSuccessToast(t('toast.removed'));
      invalidate();
    },
    onError: (e) => showErrorToast(e.message),
    onSettled: () => setBusyKey(null),
  });
  const updateSettings = trpc.postingRules.settings.update.useMutation({
    onSuccess: () => {
      showSuccessToast(t('toast.settingsSaved'));
      invalidate();
    },
    onError: (e) => showErrorToast(e.message),
  });
  const materialize = trpc.postingRules.materializeDefaults.useMutation({
    onSuccess: (r) => {
      showSuccessToast(t('toast.materialized', { created: r.created, skipped: r.skipped, missing: r.missing.length }));
      invalidate();
    },
    onError: (e) => showErrorToast(e.message),
  });

  const saveRule = (event: string, role: string, dimension: string, accountId: string) => {
    setBusyKey(`${event}|${role}|${dimension}`);
    upsert.mutate({ app, event, role, dimension, accountId });
  };
  const removeRule = (ruleId: string) => {
    setBusyKey(ruleId);
    remove.mutate({ app, id: ruleId });
  };

  const hubHref = app === 'TRAVEL' ? '/travel/accounting' : '/trading/accounting';
  const summary = data?.coverage.summary;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to={hubHref} className="mb-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-3.5 w-3.5" />
            {t('backToAccounting')}
          </Link>
          <h1 className="text-2xl font-bold">{t('title')}</h1>
          <p className="text-muted-foreground">{t(app === 'TRAVEL' ? 'subtitleTravel' : 'subtitleTrading')}</p>
        </div>
        {summary && (
          <div className="flex flex-wrap gap-2 text-xs">
            <Stat label={t('summary.byRule')} value={summary.byRule} tone="emerald" />
            <Stat label={t('summary.byDefault')} value={summary.byDefault} tone="amber" />
            <Stat label={t('summary.missing')} value={summary.missing} tone="red" />
            {summary.inactiveRules > 0 && <Stat label={t('summary.inactiveRules')} value={summary.inactiveRules} tone="red" />}
            {summary.parentAccounts > 0 && <Stat label={t('summary.parentAccounts')} value={summary.parentAccounts} tone="amber" />}
            {(data?.unresolvedFailures ?? 0) > 0 && (
              <button type="button" onClick={() => setParam('tab', 'log')} className="text-left">
                <Stat label={t('summary.failures')} value={data!.unresolvedFailures} tone="red" />
              </button>
            )}
          </div>
        )}
      </div>

      {summary && summary.missing > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-900/20 dark:text-red-100">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t('missingBanner', { count: summary.missing })}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="flex gap-4">
          {[
            { id: 'events' as const, icon: ListChecks, label: t('tabs.events') },
            { id: 'defaults' as const, icon: BookOpen, label: t('tabs.defaults') },
            { id: 'settings' as const, icon: Settings2, label: t('tabs.settings') },
            { id: 'log' as const, icon: ScrollText, label: t('tabs.log') },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setParam('tab', tab.id === 'events' ? null : tab.id)}
              className={cn(
                'flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors',
                activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.id === 'log' && (data?.unresolvedFailures ?? 0) > 0 && (
                <span className="rounded-md bg-red-100 px-1.5 py-0.5 text-[11px] font-semibold text-red-800">{data!.unresolvedFailures}</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}
      {error && <div className="border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">{error.message}</div>}

      {data && activeTab === 'events' && (
        <EventsTab
          catalog={data.catalog}
          lang={lang}
          events={data.coverage.events}
          selectedEvent={selectedEvent}
          onSelect={(e) => setParam('event', e)}
          accountOptions={accountOptions}
          canEdit={canEdit}
          busyKey={busyKey}
          onSave={saveRule}
          onRemove={removeRule}
        />
      )}

      {data && activeTab === 'defaults' && (
        <DefaultsTab
          catalog={data.catalog}
          lang={lang}
          globals={data.coverage.globals}
          accountOptions={accountOptions}
          canEdit={canEdit}
          busyKey={busyKey}
          onSave={(role, dimension, accountId) => saveRule(GLOBAL_EVENT, role, dimension, accountId)}
          onRemove={removeRule}
        />
      )}

      {data && activeTab === 'settings' && (
        <SettingsTab
          app={app}
          settings={data.settings}
          canEdit={canEdit}
          savingSettings={updateSettings.isPending}
          onUpdateSettings={(patch) => updateSettings.mutate({ app, patch })}
          onMaterialize={() => materialize.mutate({ app })}
          materializing={materialize.isPending}
        />
      )}

      {data && activeTab === 'log' && <LogTab app={app} catalog={data.catalog} lang={lang} canEdit={canEdit} />}

      {!canEdit && data && (
        <p className="text-xs text-muted-foreground">
          <Button variant="link" className="h-auto p-0 text-xs" asChild>
            <span>{t('readOnlyHint')}</span>
          </Button>
        </p>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: 'emerald' | 'amber' | 'red' }) {
  const tones = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-100',
    amber: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-100',
    red: 'border-red-200 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-900/20 dark:text-red-100',
  };
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-md border px-2 py-1', tones[tone])}>
      <span className="text-sm font-semibold tabular-nums">{value}</span>
      <span>{label}</span>
    </span>
  );
}
